'use client';
import Logo from '@/components/global/Logo';
import { Motion } from '@/components/global/utils/Motion';
import { GroqImage } from '@/helpers/Image';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CTAButton } from '@/shadcn/ui/button';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';

interface IPrinceptor {
  image: GROQImage;
  title: PortableTextBlock[];
  button: GROQButton;
  buttonMobile: GROQButton;
}

export default function PrinceptorSection({
  image,
  title,
  button,
  buttonMobile,
}: IPrinceptor) {

  return (
    <section id="princeptor-section" className="pt-[20rem] pb-[8rem] lg:pt-[37.25rem] lg:pb-[6.87rem] lg:pl-[49.75rem] lg:pr-[46.63rem] overflow-clip">
      <div className="container flex flex-col lg:flex-row items-center gap-[5rem] lg:gap-[10.87rem]">
        <motion.div
          initial={{ opacity: 0, y: '10rem' }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: [0.25, 1, 0.5, 1] }}
          className="w-[30.375rem] lg:w-[60.375rem] aspect-[67/105] relative overflow-hidden"
        >
          {image && (
            <GroqImage
              src={image}
              alt="Common Questions Image"
              className="!w-full !h-full !object-cover object-center"
            />
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: '-10rem' }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: [0.25, 1, 0.5, 1], delay: 0.5 }}
          className="flex flex-col items-center lg:items-start gap-[2rem] lg:gap-[4rem] w-full lg:w-[71.95rem] flex-shrink-0 px-[2rem] lg:px-0"
        >
          <div className="flex flex-col lg:gap-[3rem] text-center lg:text-left prose-headings:text-[#F2F0EF] prose-headings:uppercase prose-headings:leading-[110%] lg:prose-headings:leading-[120%] prose-headings:mb-[1rem] lg:prose-headings:mb-0 prose-p:text-[#F2F0EF]/[72%] prose-p:text-[2rem] prose-p:font-[300] prose-p:mb-0 prose-p:mt-[2rem] lg:prose-p:mt-0">
            {title && (
              <PortableText value={title} components={richTextComponent} />
            )}
          </div>
          {button && (
            <CTAButton data={button} className="hidden lg:block" />
          )}
          {buttonMobile && (
            <CTAButton data={buttonMobile} className="lg:hidden" />
          )}
        </motion.div>
      </div>
    </section>
  );
}
